import { inferredTagsForNote, isGoogleKeepRollbackTombstone, type Note } from '../domain/note'
import { reminderDisplayAt } from '../domain/reminder'
import {
  authoredProjectionText,
  createNoteSearchProjection,
  type NoteSearchProjection,
} from './searchProjection'
import type { NoteSearchIndex, NoteSearchResult, SearchFilters, SearchMatchOrigin } from './noteSearch'
import { matchesSearchProperty } from './noteFilters'

function matchesFilters(note: Note, filters: SearchFilters, now: number): boolean {
  if ((!filters.status || filters.status === 'all') && note.status === 'trash') return false
  if (filters.status && filters.status !== 'all' && note.status !== filters.status) return false
  if (!matchesSearchProperty(note, filters.property)) return false
  if (filters.quickAccess && !note.quickAccess) return false
  if (filters.hasAlarm && (!note.reminder || reminderDisplayAt(note.reminder, now) < now)) return false
  const requiredTags = new Set([
    ...(filters.tags ?? []),
    ...(filters.tag ? [filters.tag] : []),
  ].map((tag) => tag.toLowerCase()))
  if (requiredTags.size === 0) return true
  const noteTags = new Set([
    ...note.userTags,
    ...inferredTagsForNote(note),
  ].map((tag) => tag.toLowerCase()))
  return [...requiredTags].every((tag) => noteTags.has(tag))
}

function immediateResult(
  note: Note,
  projection: NoteSearchProjection,
  query: string,
  score: number,
  matchOrigin: SearchMatchOrigin,
  now: number,
): NoteSearchResult {
  const title = note.manualTitle ?? note.derivedTitle
  const searchable = authoredProjectionText(projection)
  const matchIndex = query ? searchable.toLowerCase().indexOf(query.toLowerCase()) : -1
  const start = Math.max(0, matchIndex - 45)
  const snippet = searchable.slice(start, start + 150)
  return {
    id: note.id,
    score,
    title,
    snippet: `${start > 0 ? '…' : ''}${snippet}`,
    matchTerms: query && matchOrigin === 'authored' ? [query] : [],
    matchOrigin,
    status: note.status,
    contentType: note.contentType,
    modifiedAt: note.modifiedAt,
    nextReminderAt: note.reminder ? reminderDisplayAt(note.reminder, now) : null,
  }
}

/** Keeps Capture light while the full fuzzy-search engine loads after first paint. */
export class DeferredNoteSearchIndex {
  private notesById = new Map<string, Note>()
  private unpackedById = new Map<string, string>()
  private delegate: NoteSearchIndex | null = null
  private loading: Promise<void> | null = null

  constructor(
    private readonly onReady: () => void = () => undefined,
  ) {}

  load(notes: Note[]): void {
    const visibleNotes = notes.filter((note) => !isGoogleKeepRollbackTombstone(note))
    this.notesById = new Map(visibleNotes.map((note) => [note.id, note]))
    if (this.delegate) this.delegate.load(visibleNotes)
    else void this.ensureLoaded()
  }

  addBatch(notes: Note[]): void {
    for (const note of notes) {
      if (isGoogleKeepRollbackTombstone(note)) {
        this.remove(note.id)
      } else {
        this.notesById.set(note.id, note)
      }
    }
    this.delegate?.addBatch(notes)
  }

  upsert(note: Note): void {
    if (isGoogleKeepRollbackTombstone(note)) {
      this.remove(note.id)
      return
    }
    this.notesById.set(note.id, note)
    this.delegate?.upsert(note)
  }

  remove(noteId: string): void {
    this.notesById.delete(noteId)
    this.delegate?.remove(noteId)
  }

  /** Every unpacked note's words from its screenshots (NoteSearchIndex.loadUnpacked). */
  loadUnpacked(words: ReadonlyMap<string, string>): void {
    this.unpackedById = new Map(words)
    this.delegate?.loadUnpacked(words)
  }

  /** One note's unpacked words, or null once it was undone. */
  setUnpacked(noteId: string, words: string | null): void {
    if (words) this.unpackedById.set(noteId, words)
    else this.unpackedById.delete(noteId)
    this.delegate?.setUnpacked(noteId, words)
  }

  search(query: string, filters: SearchFilters = {}): NoteSearchResult[] {
    if (this.delegate) return this.delegate.search(query, filters)
    const normalizedQuery = query.trim()
    const terms = normalizedQuery.toLowerCase().split(/\s+/).filter(Boolean)
    const now = Date.now()
    return [...this.notesById.values()].map((note) => ({
      note,
      projection: createNoteSearchProjection(note, this.unpackedById.get(note.id) ?? ''),
    }))
      .filter(({ note }) => matchesFilters(note, filters, now))
      .map(({ note, projection }) => {
        if (terms.length === 0) {
          return { note, projection, matchOrigin: 'recent' as const }
        }
        const authored = authoredProjectionText(projection).toLowerCase()
        if (terms.every((term) => authored.includes(term))) {
          return { note, projection, matchOrigin: 'authored' as const }
        }
        return null
      })
      .map((entry, index) => entry && ({ ...entry, sourceIndex: index }))
      .filter((entry): entry is {
        note: Note
        projection: NoteSearchProjection
        matchOrigin: SearchMatchOrigin
        sourceIndex: number
      } => entry !== null)
      .sort((left, right) => filters.hasAlarm
        ? (left.note.reminder
            ? reminderDisplayAt(left.note.reminder, now)
            : Number.POSITIVE_INFINITY)
          - (right.note.reminder
            ? reminderDisplayAt(right.note.reminder, now)
            : Number.POSITIVE_INFINITY)
          || right.note.modifiedAt - left.note.modifiedAt
          || left.note.id.localeCompare(right.note.id)
        : left.matchOrigin.localeCompare(right.matchOrigin)
          || right.note.modifiedAt - left.note.modifiedAt)
      .map(({ note, projection, matchOrigin }, index) =>
        immediateResult(
          note,
          projection,
          normalizedQuery,
          1 / (index + 1),
          matchOrigin,
          now,
        ))
  }

  private ensureLoaded(): Promise<void> {
    this.loading ??= import('./noteSearch').then(({ NoteSearchIndex }) => {
      const delegate = new NoteSearchIndex()
      delegate.loadUnpacked(this.unpackedById)
      delegate.load([...this.notesById.values()])
      this.delegate = delegate
      this.onReady()
    })
    return this.loading
  }
}
